/** Ítem afecto a IGV (booleano estricto; evita que "false" string sea truthy). */
export const isItemTaxable = (hasIgv) =>
  hasIgv === true || hasIgv === 'true' || hasIgv === 1;

/** Factor multiplicador: 1 + tasa/100 (ej. 18% → 1.18). */
export const getIgvFactor = (igvRate) => {
  const rate = parseFloat(igvRate);
  if (!Number.isFinite(rate) || rate <= 0) return 1;
  return 1 + rate / 100;
};

export const roundPrice = (value, decimals = 4) => {
  const n = parseFloat(value);
  if (!Number.isFinite(n)) return 0;
  const factor = 10 ** decimals;
  return Math.round(n * factor) / factor;
};

/** Redondeo monetario para pantalla (2 decimales). */
export const roundMoney = (value) => roundPrice(value, 2);

/** Neto monetario → bruto monetario (2 decimales, p. ej. descuento con IGV). */
export const netToGrossMoney = (netPrice, igvRate) => {
  const net = roundMoney(netPrice);
  if (!Number.isFinite(net)) return 0;
  return roundMoney(net * getIgvFactor(igvRate));
};

/** Neto (sin IGV) → bruto (con IGV) para cálculos internos. */
export const netToGross = (netPrice, igvRate) => {
  const net = parseFloat(netPrice);
  if (!Number.isFinite(net)) return 0;
  return roundPrice(net * getIgvFactor(igvRate));
};

/**
 * Bruto para el input en modo "Con IGV": hasta 4 decimales.
 * Si el valor a 4 decimales coincide con el centavo (ej. 20.0001 → 20), muestra el monto limpio.
 */
export const netToGrossDisplay = (netPrice, igvRate) => {
  const net = parseFloat(netPrice);
  if (!Number.isFinite(net)) return 0;
  const gross4 = roundPrice(net * getIgvFactor(igvRate), 4);
  const gross2 = roundMoney(gross4);
  if (Math.abs(gross4 - gross2) <= 0.0001) return gross2;
  return gross4;
};

/** Bruto (con IGV) → neto (sin IGV). Siempre lo que se persiste en BD. */
export const grossToNet = (grossPrice, igvRate) => {
  const gross = parseFloat(grossPrice);
  if (!Number.isFinite(gross)) return 0;
  const factor = getIgvFactor(igvRate);
  if (factor <= 0) return roundPrice(gross);
  return roundPrice(gross / factor);
};

/**
 * Convierte el valor del input a precio neto para almacenar en estado/BD.
 */
export const parseUnitPriceForStorage = (inputValue, { pricesIncludeIgv, hasIgv, igvRate }) => {
  if (inputValue === '' || inputValue === null || inputValue === undefined) return 0;
  const raw = parseFloat(inputValue);
  if (!Number.isFinite(raw)) return 0;
  if (pricesIncludeIgv && isItemTaxable(hasIgv)) {
    return grossToNet(raw, igvRate);
  }
  return roundPrice(raw);
};

/**
 * Valor a mostrar en el input según la palanca (el estado interno siempre es neto).
 */
export const getDisplayUnitPrice = (netPrice, { pricesIncludeIgv, hasIgv, igvRate }) => {
  if (netPrice === '' || netPrice === null || netPrice === undefined) return '';
  const net = parseFloat(netPrice);
  if (!Number.isFinite(net)) return '';
  if (net === 0) return 0;
  if (pricesIncludeIgv && isItemTaxable(hasIgv)) {
    return netToGrossDisplay(net, igvRate);
  }
  return roundPrice(net);
};

/**
 * Al pasar un ítem de gravado a no gravado con precios "Con IGV",
 * conserva el monto que el usuario veía en el input como precio literal.
 */
export const adjustNetPriceOnHasIgvChange = (
  netPrice,
  { oldHasIgv, newHasIgv, pricesIncludeIgv, igvRate },
) => {
  const net = parseFloat(netPrice);
  if (!Number.isFinite(net) || net <= 0) return 0;
  if (!pricesIncludeIgv) return roundPrice(net);
  const wasTaxable = isItemTaxable(oldHasIgv);
  const nowTaxable = isItemTaxable(newHasIgv);
  if (wasTaxable && !nowTaxable) {
    return roundPrice(netToGrossDisplay(net, igvRate));
  }
  return roundPrice(net);
};

/** Neto en hints: hasta 4 decimales (lo que se guarda en BD). */
const formatNetMoney = (amount) =>
  roundPrice(amount, 4).toLocaleString('es-PE', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 4,
  });

/** Bruto en hints: 2 decimales (monto comercial, evita 20.0001). */
const formatGrossMoney = (amount) =>
  roundMoney(amount).toLocaleString('es-PE', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

/**
 * Hint bajo Total: siempre muestra el monto con IGV (sin depender de Sin/Con IGV).
 */
export const getLineTotalConIgvHint = (netTotal, { hasIgv, igvRate }) => {
  if (!isItemTaxable(hasIgv)) return null;
  const net = parseFloat(netTotal);
  if (!Number.isFinite(net) || net <= 0) return null;
  const gross = net * getIgvFactor(igvRate);
  return { text: `= S/ ${formatGrossMoney(gross)} con IGV` };
};

/**
 * Total general referencial con IGV (guía de entrada): suma líneas gravadas al bruto.
 */
export const getGuideGrandTotalConIgvReferential = (items, igvRate = 18) => {
  let total = 0;
  let hasTaxableLine = false;

  (items || []).forEach((item) => {
    const net = parseFloat(item?.total) || 0;
    if (net <= 0) return;
    if (isItemTaxable(item?.has_igv ?? true)) {
      hasTaxableLine = true;
      total += net * getIgvFactor(igvRate);
    } else {
      total += net;
    }
  });

  if (!hasTaxableLine || total <= 0) return null;
  return formatGrossMoney(total);
};

/**
 * Texto de ayuda bajo el precio (patrón similar a conversión de kilajes).
 */
export const getPriceIgvHint = (netPrice, { pricesIncludeIgv, hasIgv, igvRate }) => {
  if (!isItemTaxable(hasIgv)) return null;
  const net = parseFloat(netPrice);
  if (!Number.isFinite(net) || net <= 0) return null;

  if (pricesIncludeIgv) {
    return { text: `= S/ ${formatNetMoney(net)} sin IGV` };
  }

  const gross = net * getIgvFactor(igvRate);
  return { text: `= S/ ${formatGrossMoney(gross)} con IGV` };
};
